import { useNavigate } from 'react-router-dom'
import { useOrgPath } from 'hooks/useOrgPath'
import {
  CardMeta,
  CardMetaGrid,
  ManagementCard,
  ManagementCardList,
  Pill,
} from 'components/ManagementCard'
import { AdCodeBadge } from 'components/AdCodeBadge'
import { TagChip } from 'components/TagChip'
import { commonStrings, userStrings } from 'constants/strings'
import type { TLineFriendListItem, TLineFriendStatus } from 'types'
import {
  buildUserEditPath,
  formatLineFriendBasicId,
  formatLineFriendDateTime,
  getFriendStatusLabel,
} from './lineFriendView'
import { TestUserSwitch } from './TestUserSwitch'

const friendStatusTone: Record<
  TLineFriendStatus,
  'accent' | 'danger' | 'neutral'
> = {
  NORMAL: 'accent',
  BLOCKED: 'danger',
  NO_LINKED_CHANNEL: 'neutral',
}

type UsersCardListProps = {
  users: TLineFriendListItem[]
  loading: boolean
  emptyMessage?: string
  onToggleTestUser: (userLineId: number, isTestUser: boolean) => void
  pendingTestUserIds: number[]
  className?: string
}

export function UsersCardList({
  users,
  loading,
  emptyMessage = userStrings.states.empty,
  onToggleTestUser,
  pendingTestUserIds,
  className,
}: UsersCardListProps) {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()

  return (
    <ManagementCardList
      loading={loading}
      isEmpty={users.length === 0}
      loadingMessage={commonStrings.loading}
      emptyMessage={emptyMessage}
      className={className}
    >
      {users.map((user) => (
        <li key={user.id}>
          <UserCard
            user={user}
            onEdit={() => navigate(orgPathFor(buildUserEditPath(user.id)))}
            onToggleTestUser={onToggleTestUser}
            isTestUserPending={pendingTestUserIds.includes(user.id)}
          />
        </li>
      ))}
    </ManagementCardList>
  )
}

type UserCardProps = {
  user: TLineFriendListItem
  onEdit: () => void
  onToggleTestUser: (userLineId: number, isTestUser: boolean) => void
  isTestUserPending: boolean
}

function UserCard({
  user,
  onEdit,
  onToggleTestUser,
  isTestUserPending,
}: UserCardProps) {
  const emptyValue = userStrings.table.emptyValue
  // ステータスバッジが「LINE連携なし」を示すため、本文側は重複を避けて空表示にする
  const linkedBasicId = formatLineFriendBasicId(user.linkedBasicId) ?? emptyValue
  const lastAccessAt = formatLineFriendDateTime(user.lastAccessAt) ?? emptyValue
  const friendRegisteredAt =
    formatLineFriendDateTime(user.friendRegisteredAt) ?? emptyValue

  // テストユーザートグルは actions スロット（カード全面タップ用オーバーレイより前面）に
  // 置き、スイッチ操作が編集画面遷移と競合しないようにする
  return (
    <ManagementCard
      onClick={onEdit}
      ariaLabel={`${user.lineName} ${commonStrings.actions.edit}`}
      title={user.lineName}
      titleSuffix={`#${user.id}`}
      badges={
        <Pill tone={friendStatusTone[user.friendStatus]} className="ml-auto">
          {getFriendStatusLabel(user.friendStatus)}
        </Pill>
      }
      actions={
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted">
            {userStrings.table.headers.isTestUser}
          </span>
          <TestUserSwitch
            checked={user.isTestUser}
            disabled={isTestUserPending}
            ariaLabel={`${userStrings.table.headers.isTestUser} ${user.id}`}
            onChange={(next) => onToggleTestUser(user.id, next)}
          />
        </div>
      }
    >
      <p className="truncate text-xs text-muted">{linkedBasicId}</p>
      {user.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {user.tags.map((tag) => (
            <TagChip
              key={tag.id}
              name={tag.name}
              color={tag.color}
              variant="solid"
            />
          ))}
        </div>
      )}
      {user.adCode && (
        <AdCodeBadge
          name={user.adCode.name}
          confidence={user.adCode.confidence}
          className="mt-1"
        />
      )}
      <CardMetaGrid>
        <CardMeta
          label={userStrings.table.headers.lastAccessAt}
          value={lastAccessAt}
        />
        <CardMeta
          label={userStrings.table.headers.friendRegisteredAt}
          value={friendRegisteredAt}
        />
      </CardMetaGrid>
    </ManagementCard>
  )
}
